/**
 * Typed fetch wrapper for the WorkSim API. Every request carries the session cookie and the
 * `X-Requested-With` header the server requires on state-changing calls (CSRF defense). Errors
 * are normalized into `ApiError` so pages never render raw error objects.
 */
import type {
  AdminScenarioDetail,
  AdminScenarioSummary,
  AiStatus,
  ApiErrorBody,
  Assignment,
  AuditEntry,
  CareerDetail,
  CareersResponse,
  CertificationSummary,
  Comparison,
  ContentScenario,
  ContentStatus,
  DatasetDetail,
  DatasetSummary,
  DeliverableRecord,
  DepartmentDetail,
  DirectoryPerson,
  EmployeeDetail,
  EvaluationSummary,
  InterviewAnswerResult,
  InterviewKitDetail,
  InterviewKitSummary,
  InterviewReadinessResponse,
  InterviewSessionSummary,
  InterviewSessionView,
  LabAnswerResult,
  LabExecuteResult,
  LabSessionView,
  LabSummary,
  LearnProgress,
  LessonResult,
  LessonSummary,
  MentorMessage,
  MentorReply,
  MentorRole,
  Notification,
  PathSummary,
  PathView,
  Profile,
  ProfilePatch,
  ProgramDay,
  ProgramView,
  ProgressDashboard,
  PublicLab,
  RequestedScenarioLevel,
  PublicLesson,
  RoleplayState,
  SandboxRunResult,
  ScenarioSandboxView,
  ScenarioView,
  SessionAction,
  SessionEvaluation,
  SessionMutation,
  SessionSummary,
  SessionView,
  SqlExecuteResponse,
  StepSubmission,
  ToolActionSubmission,
  TrainingMode,
  Transition,
  User,
  ValidationResult,
  WorkplaceEvent,
  WorkplaceOverview,
} from './types';
import type { CareerProgressReport, Certification, InterviewMode, KnowledgeArticle, LabMode, LabReport, Policy, QualitativeFeedback, SharedDocument, SkillDefinition } from '@worksim/engine';

export const API_BASE = '/api';
export const CSRF_HEADER = 'X-Requested-With';
export const CSRF_VALUE = 'WorkSim';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Query = Record<string, string | number | boolean | undefined>;

function withQuery(path: string, query?: Query): string {
  if (!query) return path;
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== '') q.set(k, String(v));
  const s = q.toString();
  return s ? `${path}?${s}` : path;
}

const unauthorizedListeners = new Set<() => void>();
/** Register a callback for 401 responses so the app can drop a stale login. Returns an unsubscribe function. */
export function onUnauthorized(handler: () => void): () => void {
  unauthorizedListeners.add(handler);
  return () => unauthorizedListeners.delete(handler);
}

function isErrorBody(x: unknown): x is ApiErrorBody {
  return typeof x === 'object' && x !== null && 'error' in x && typeof (x as { error: unknown }).error === 'object';
}

export async function request<T>(method: string, path: string, body?: unknown, query?: Query): Promise<T> {
  const headers: Record<string, string> = { [CSRF_HEADER]: CSRF_VALUE, Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  let res: Response;
  try {
    res = await fetch(API_BASE + withQuery(path, query), { method, headers, credentials: 'include', body: body === undefined ? undefined : JSON.stringify(body) });
  } catch {
    throw new ApiError(0, 'network', 'Could not reach the WorkSim server. Check your connection and try again.');
  }
  const text = res.status === 204 ? '' : await res.text();
  let data: unknown;
  try {
    data = text ? JSON.parse(text) : undefined;
  } catch {
    data = undefined;
  }
  if (!res.ok) {
    const err = isErrorBody(data) ? data.error : undefined;
    if (res.status === 401) unauthorizedListeners.forEach((fn) => fn());
    throw new ApiError(res.status, err?.code ?? 'http_error', err?.message ?? `The server returned an error (${res.status}).`, err?.details, err?.requestId);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>('GET', path, undefined, query),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
};

/** A message safe to show a person, whatever was thrown. */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message || 'Something went wrong.';
  return 'Something went wrong.';
}

/** Field-level validation messages from a 400 `validation_failed` response. */
export function validationIssues(e: unknown): { path: string; message: string }[] {
  if (!(e instanceof ApiError) || !Array.isArray(e.details)) return [];
  return e.details.filter((d): d is { path: string; message: string } => typeof d === 'object' && d !== null && 'message' in d).map((d) => ({ path: String(d.path ?? ''), message: String(d.message) }));
}

const enc = encodeURIComponent;

export const auth = {
  me: () => api.get<{ user: User }>('/auth/me'),
  login: (email: string, password: string) => api.post<{ user: User }>('/auth/login', { email, password }),
  register: (email: string, password: string, displayName: string) => api.post<{ user: User }>('/auth/register', { email, password, displayName }),
  logout: () => api.post<void>('/auth/logout'),
  changePassword: (currentPassword: string, newPassword: string) => api.post<void>('/auth/change-password', { currentPassword, newPassword }),
  /** Always accepted, whether or not the address has an account. */
  forgot: (email: string) => api.post<{ ok: true }>('/auth/forgot', { email }),
  reset: (token: string, newPassword: string) => api.post<{ ok: true }>('/auth/reset', { token, newPassword }),
  verify: (token: string) => api.post<{ ok: true; verified: true }>('/auth/verify', { token }),
  resendVerification: () => api.post<{ ok: true }>('/auth/resend-verification'),
};

export const account = {
  profile: () => api.get<{ profile: Profile }>('/account/profile'),
  update: (patch: ProfilePatch) => api.patch<{ profile: Profile }>('/account/profile', patch),
  exportData: () => api.get<Record<string, unknown>>('/account/export'),
  deleteAccount: (password: string) => api.delete<void>('/account', { password }),
};

export const catalog = {
  careers: (query?: { q?: string; family?: string; available?: boolean }) => api.get<CareersResponse>('/catalog/careers', { q: query?.q, family: query?.family, available: query?.available ? 'true' : undefined }),
  career: (id: string) => api.get<CareerDetail>(`/catalog/careers/${enc(id)}`),
  compare: (a: string, b: string) => api.get<{ comparison: Comparison }>('/catalog/compare', { a, b }),
  skills: () => api.get<{ skills: SkillDefinition[] }>('/catalog/skills'),
  scenarios: () => api.get<{ scenarios: ScenarioView[] }>('/catalog/scenarios'),
  scenario: (id: string, career?: string) => api.get<{ scenario: ScenarioView }>(`/catalog/scenarios/${enc(id)}`, { career }),
  datasets: () => api.get<{ datasets: DatasetSummary[] }>('/catalog/datasets'),
  dataset: (id: string) => api.get<{ dataset: DatasetDetail }>(`/catalog/datasets/${enc(id)}`),
};

export const progress = {
  dashboard: () => api.get<{ progress: ProgressDashboard }>('/progress'),
  career: (id: string) => api.get<{ progress: CareerProgressReport }>(`/progress/careers/${enc(id)}`),
  transition: (from: string, to: string) => api.get<{ transition: Transition }>('/progress/transition', { from, to }),
  evaluations: () => api.get<{ evaluations: EvaluationSummary[] }>('/progress/evaluations'),
};

export const sessions = {
  list: () => api.get<{ sessions: SessionSummary[] }>('/sessions'),
  /** `level` picks a difficulty variant; 'auto' (the default) lets the skill matrix choose. */
  start: (scenarioId: string, careerId: string, trainingMode?: string, level?: RequestedScenarioLevel) => api.post<{ session: SessionView }>('/sessions', { scenarioId, careerId, trainingMode, level }),
  get: (id: string) => api.get<{ session: SessionView }>(`/sessions/${enc(id)}`),
  actions: (id: string) => api.get<{ actions: SessionAction[] }>(`/sessions/${enc(id)}/actions`),
  evaluation: (id: string) => api.get<{ evaluation: SessionEvaluation }>(`/sessions/${enc(id)}/evaluation`),
  submitStep: (id: string, result: StepSubmission) => api.post<SessionMutation>(`/sessions/${enc(id)}/steps`, result),
  toolAction: (id: string, action: ToolActionSubmission) => api.post<SessionMutation>(`/sessions/${enc(id)}/tool-actions`, action),
  roleplayOpening: (id: string) => api.get<RoleplayState>(`/sessions/${enc(id)}/roleplay`),
  roleplayTurn: (id: string, text: string) => api.post<RoleplayState>(`/sessions/${enc(id)}/roleplay`, { text }),
  roleplayFinish: (id: string) => api.post<SessionMutation>(`/sessions/${enc(id)}/roleplay/finish`),
  handoff: (id: string, message: string) => api.post<SessionMutation>(`/sessions/${enc(id)}/handoff`, { message }),
  /** Hands-on step: the draft sandbox state, initialized on the first call. */
  sandbox: (id: string) => api.get<{ sandbox: ScenarioSandboxView }>(`/sessions/${enc(id)}/sandbox`),
  sandboxRun: (id: string, sandboxId: string, input: string) => api.post<SandboxRunResult>(`/sessions/${enc(id)}/sandbox/run`, { sandboxId, input }),
  sandboxSubmit: (id: string) => api.post<SessionMutation>(`/sessions/${enc(id)}/sandbox/submit`),
  hint: (id: string) => api.post<{ hint?: string }>(`/sessions/${enc(id)}/hint`),
  abandon: (id: string) => api.post<{ session: SessionView }>(`/sessions/${enc(id)}/abandon`),
  replay: (id: string) => api.post<{ session: SessionView }>(`/sessions/${enc(id)}/replay`),
};

/**
 * The LEARN layer: certification-aligned lessons, hands-on labs and the learn → practice → apply
 * paths that sequence them. The catalog calls are public; everything carrying learner state
 * (paths with per-item progress, submissions, lab sessions) needs a signed-in account.
 */
export const learn = {
  certifications: () => api.get<{ certifications: CertificationSummary[]; notice: string }>('/learn/certifications'),
  certification: (id: string) => api.get<{ certification: Certification; notice: string }>(`/learn/certifications/${enc(id)}`),
  lessons: (career?: string) => api.get<{ lessons: LessonSummary[] }>('/learn/lessons', { career }),
  lesson: (id: string) => api.get<{ lesson: PublicLesson }>(`/learn/lessons/${enc(id)}`),
  submitLesson: (id: string, answers: Record<string, number>) => api.post<{ result: LessonResult }>(`/learn/lessons/${enc(id)}/submit`, { answers }),
  labs: (career?: string) => api.get<{ labs: LabSummary[]; notice: string }>('/learn/labs', { career }),
  lab: (id: string) => api.get<{ lab: PublicLab }>(`/learn/labs/${enc(id)}`),
  paths: () => api.get<{ paths: PathSummary[] }>('/learn/paths'),
  /** Accepts a path id or a career id. */
  path: (idOrCareer: string) => api.get<{ path: PathView }>(`/learn/paths/${enc(idOrCareer)}`),
  progress: () => api.get<LearnProgress>('/learn/progress'),
  startLab: (labId: string, mode: LabMode) => api.post<{ session: LabSessionView }>('/learn/lab-sessions', { labId, mode }),
  labSession: (id: string) => api.get<{ session: LabSessionView }>(`/learn/lab-sessions/${enc(id)}`),
  execute: (id: string, sandboxId: string, input: string) => api.post<LabExecuteResult>(`/learn/lab-sessions/${enc(id)}/execute`, { sandboxId, input }),
  answer: (id: string, questionId: string, value: string) => api.post<LabAnswerResult>(`/learn/lab-sessions/${enc(id)}/answer`, { questionId, value }),
  hint: (id: string) => api.post<{ hint?: string }>(`/learn/lab-sessions/${enc(id)}/hint`),
  completeLab: (id: string) => api.post<{ report: LabReport; session: LabSessionView }>(`/learn/lab-sessions/${enc(id)}/complete`),
  abandonLab: (id: string) => api.post<{ session: LabSessionView }>(`/learn/lab-sessions/${enc(id)}/abandon`),
};

/**
 * Interview preparation: the kit for a career (how its interviews run, the question bank and the
 * rounds a mock interview walks), the readiness gate that unlocks it, and practice or mock
 * sessions. Kits are public; readiness and sessions need a signed-in account. Starting a session
 * for a career the learner has not mastered answers 409 with the readiness in the error details.
 */
export const interview = {
  kits: () => api.get<{ kits: InterviewKitSummary[]; notice: string }>('/interview/kits'),
  kit: (careerId: string) => api.get<{ kit: InterviewKitDetail; notice: string }>(`/interview/kits/${enc(careerId)}`),
  readiness: (careerId: string) => api.get<InterviewReadinessResponse>('/interview/readiness', { career: careerId }),
  sessions: () => api.get<{ sessions: InterviewSessionSummary[] }>('/interview/sessions'),
  start: (careerId: string, mode: InterviewMode) => api.post<{ session: InterviewSessionView }>('/interview/sessions', { careerId, mode }),
  session: (id: string) => api.get<{ session: InterviewSessionView }>(`/interview/sessions/${enc(id)}`),
  answer: (id: string, questionId: string, text: string) => api.post<InterviewAnswerResult>(`/interview/sessions/${enc(id)}/answer`, { questionId, text }),
  /** Practical questions: the completed challenge lab session stands in for a written answer. */
  answerLab: (id: string, questionId: string, labSessionId: string) => api.post<InterviewAnswerResult>(`/interview/sessions/${enc(id)}/answer-lab`, { questionId, labSessionId }),
  /** Mock mode: move to the next round once every question in this one is answered. */
  next: (id: string) => api.post<{ session: InterviewSessionView }>(`/interview/sessions/${enc(id)}/next`),
  complete: (id: string) => api.post<{ session: InterviewSessionView }>(`/interview/sessions/${enc(id)}/complete`),
  abandon: (id: string) => api.post<{ session: InterviewSessionView }>(`/interview/sessions/${enc(id)}/abandon`),
};

export const deliverables = {
  list: () => api.get<{ deliverables: DeliverableRecord[] }>('/deliverables'),
  get: (id: string) => api.get<{ deliverable: DeliverableRecord }>(`/deliverables/${enc(id)}`),
};

export const workplace = {
  overview: () => api.get<{ workplace: WorkplaceOverview }>('/workplace'),
  department: (id: string) => api.get<{ department: DepartmentDetail }>(`/workplace/departments/${enc(id)}`),
  directory: (q?: string) => api.get<{ people: DirectoryPerson[] }>('/workplace/directory', { q }),
  employee: (id: string) => api.get<{ employee: EmployeeDetail }>(`/workplace/employees/${enc(id)}`),
  policies: () => api.get<{ policies: Policy[] }>('/workplace/policies'),
  knowledgeBase: (q?: string) => api.get<{ articles: KnowledgeArticle[] }>('/workplace/knowledge-base', { q }),
  documents: () => api.get<{ documents: SharedDocument[] }>('/workplace/documents'),
  assignments: () => api.get<{ assignments: Assignment[] }>('/workplace/assignments'),
  requestAssignment: (scenarioId: string) => api.post<{ assignment: Assignment }>('/workplace/assignments', { scenarioId }),
  notifications: (unreadOnly?: boolean) => api.get<{ notifications: Notification[] }>('/workplace/notifications', { unread: unreadOnly ? 'true' : undefined }),
  markRead: (id: string) => api.post<void>(`/workplace/notifications/${enc(id)}/read`),
  events: () => api.get<{ events: WorkplaceEvent[] }>('/workplace/events'),
};

export const sql = {
  execute: (datasetId: string, sqlText: string) => api.post<SqlExecuteResponse>('/sql/execute', { datasetId, sql: sqlText }),
};

export const ai = {
  status: () => api.get<AiStatus>('/ai/status'),
  mentorHistory: (sessionId: string) => api.get<{ history: MentorMessage[] }>(`/ai/sessions/${enc(sessionId)}/mentor`),
  mentor: (sessionId: string, message: string, role?: MentorRole, mode?: TrainingMode) => api.post<MentorReply>(`/ai/sessions/${enc(sessionId)}/mentor`, { message, role, mode }),
  qualitativeFeedback: (sessionId: string) => api.post<{ qualitative: QualitativeFeedback }>(`/ai/sessions/${enc(sessionId)}/qualitative-feedback`),
};

export const admin = {
  scenarios: (q?: string) => api.get<{ scenarios: AdminScenarioSummary[] }>('/admin/scenarios', { q }),
  scenario: (id: string) => api.get<AdminScenarioDetail>(`/admin/scenarios/${enc(id)}`),
  saveDraft: (id: string, definition: unknown, changeNote: string) => api.put<{ version: number; validation: ValidationResult }>(`/admin/scenarios/${enc(id)}`, { definition, changeNote }),
  validate: (id: string, definition: unknown) => api.post<ValidationResult>(`/admin/scenarios/${enc(id)}/validate`, { definition }),
  transition: (id: string, to: ContentStatus) => api.post<{ content: ContentScenario }>(`/admin/scenarios/${enc(id)}/transition`, { to }),
  rollback: (id: string, version: number) => api.post<{ version: number; status: ContentStatus }>(`/admin/scenarios/${enc(id)}/rollback`, { version }),
  preview: (id: string, version?: number) => api.get<{ scenario: ScenarioView }>(`/admin/scenarios/${enc(id)}/preview`, { version }),
  auditLog: () => api.get<{ entries: AuditEntry[] }>('/admin/audit-log'),
};

export const program = {
  current: () => api.get<{ program: ProgramView | null }>('/program'),
  template: (careerId: string) => api.get<{ days: ProgramDay[] }>(`/program/template/${enc(careerId)}`),
  start: (careerId: string) => api.post<{ program: ProgramView }>('/program/start', { careerId }),
  completeTask: (day: number, taskId: string, payload?: unknown) => api.post<{ program: ProgramView }>('/program/tasks/complete', { day, taskId, payload }),
  advance: () => api.post<{ program: ProgramView }>('/program/advance'),
  pause: () => api.post<{ program: ProgramView }>('/program/pause'),
  resume: () => api.post<{ program: ProgramView }>('/program/resume'),
  goTo: (day: number) => api.post<{ program: ProgramView }>('/program/go-to', { day }),
  replay: (day: number) => api.post<{ program: ProgramView }>('/program/replay', { day }),
};

